/**
 * @file: AiWorkshopTile.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { aiColors, aiElevation, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { AiWorkshopTool } from './useAiWorkshopTools';

/** One workshop tool on the AI home. */
export const AiWorkshopTile = memo(
  ({ tool, onPress }: { tool: AiWorkshopTool; onPress: () => void }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const Icon = tool.icon;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={tool.title}
        onPress={onPress}
        style={({ pressed }) => [styles.tile, pressed && { opacity: 0.8 }]}
      >
        <View
          style={[styles.icon, { backgroundColor: tint(tool.color, 0.15) }]}
        >
          <Icon size={16} color={tool.color} />
        </View>
        <Typography.Text
          bold
          size={rpx(14)}
          color={aiColors.foreground}
          style={{ marginTop: rpx(12) }}
        >
          {tool.title}
        </Typography.Text>
      </Pressable>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        tile: {
          flexBasis: '46%',
          flexGrow: 1,
          backgroundColor: aiColors.card,
          borderRadius: rpx(14),
          borderWidth: 1,
          borderColor: aiColors.border,
          padding: rpx(12),
          ...aiElevation.card,
        },
        icon: {
          width: rpx(32),
          height: rpx(32),
          borderRadius: rpx(16),
          alignItems: 'center',
          justifyContent: 'center',
        },
      }),
    [rpx],
  );
}
